import { z } from "zod";

/** Agent config key that lists workflow actions exposed as tools. */
export const WORKFLOW_ACTIONS_CONFIG_KEY = "workflowActions";

export const WORKFLOW_ACTION_BINDINGS_MAX = 20;

const TOOL_NAME_PREFIX = "workflow_";
const TOOL_NAME_MAX = 64;
const PARAM_NAME_MAX = 64;

export const workflowActionBindingSchema = z.object({
  workflowId: z.uuid(),
});

export type WorkflowActionBinding = z.infer<typeof workflowActionBindingSchema>;

/**
 * Read `config.workflowActions`. Invalid rows are dropped so one bad entry
 * cannot hide the rest of an agent's tools. Duplicate workflow ids collapse
 * to the first.
 */
export function parseWorkflowActionBindings(
  config: unknown,
): WorkflowActionBinding[] {
  if (!config || typeof config !== "object") return [];
  const raw = (config as Record<string, unknown>)[WORKFLOW_ACTIONS_CONFIG_KEY];
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  const bindings: WorkflowActionBinding[] = [];
  for (const item of raw) {
    const parsed = workflowActionBindingSchema.safeParse(item);
    if (!parsed.success || seen.has(parsed.data.workflowId)) continue;
    seen.add(parsed.data.workflowId);
    bindings.push(parsed.data);
    if (bindings.length >= WORKFLOW_ACTION_BINDINGS_MAX) break;
  }
  return bindings;
}

export function withWorkflowActionBinding(
  config: Record<string, unknown>,
  workflowId: string,
): Record<string, unknown> {
  const current = parseWorkflowActionBindings(config);
  if (
    current.some((binding) => binding.workflowId === workflowId) ||
    current.length >= WORKFLOW_ACTION_BINDINGS_MAX
  ) {
    return config;
  }
  return {
    ...config,
    [WORKFLOW_ACTIONS_CONFIG_KEY]: [...current, { workflowId }],
  };
}

export function withoutWorkflowActionBinding(
  config: Record<string, unknown>,
  workflowId: string,
): Record<string, unknown> {
  const current = parseWorkflowActionBindings(config).filter(
    (binding) => binding.workflowId !== workflowId,
  );
  return {
    ...config,
    [WORKFLOW_ACTIONS_CONFIG_KEY]: current,
  };
}

function slug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** A model-facing tool name: `prefix` plus the display name as a slug, or `fallback`. */
export function actionToolName(
  prefix: string,
  fallback: string,
  displayName: string,
): string {
  const body = slug(displayName).slice(0, TOOL_NAME_MAX - prefix.length);
  const base = body.length > 0 ? `${prefix}${body}` : fallback;
  return base.slice(0, TOOL_NAME_MAX);
}

/** `base`, suffixed `_2`, `_3`… past the names already used; `described` names it in the error. */
export function uniqueActionToolName(
  base: string,
  used: Set<string>,
  described: string,
): string {
  if (!used.has(base)) return base;
  for (let index = 2; index < 100; index += 1) {
    const suffix = `_${index}`;
    const candidate = `${base.slice(0, TOOL_NAME_MAX - suffix.length)}${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
  throw new Error(`Could not assign a tool name for ${described}`);
}

/** Model-facing tool name for a workflow action. Stable for a given display name. */
export function workflowActionToolName(workflowName: string): string {
  return actionToolName(TOOL_NAME_PREFIX, "workflow_action", workflowName);
}

export function uniqueWorkflowActionToolName(
  workflowName: string,
  used: Set<string>,
): string {
  return uniqueActionToolName(
    workflowActionToolName(workflowName),
    used,
    `workflow "${workflowName}"`,
  );
}

export function actionPortParamName(
  displayName: string,
  portId: string,
): string {
  const body = slug(displayName).replace(/^(\d)/, "_$1");
  const base = body.length > 0 ? body : `port_${slug(portId) || "value"}`;
  return base.slice(0, PARAM_NAME_MAX);
}

export interface ActionPortParam {
  portId: string;
  param: string;
}

export function assignPortParams(
  ports: Array<{ id: string; name: string }>,
): ActionPortParam[] {
  const used = new Set<string>();
  return ports.map((port) => {
    const base = actionPortParamName(port.name, port.id);
    let param = base;
    let index = 2;
    while (used.has(param)) {
      const suffix = `_${index}`;
      index += 1;
      param = `${base.slice(0, PARAM_NAME_MAX - suffix.length)}${suffix}`;
    }
    used.add(param);
    return { portId: port.id, param };
  });
}

export function mapParamsToPorts(
  args: Record<string, unknown>,
  params: ActionPortParam[],
): Record<string, unknown> {
  const inputs: Record<string, unknown> = {};
  for (const { portId, param } of params) {
    if (args[param] !== undefined) inputs[portId] = args[param];
  }
  return inputs;
}

export function mapPortsToParams(
  outputs: Record<string, unknown>,
  params: ActionPortParam[],
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const { portId, param } of params) {
    if (Object.prototype.hasOwnProperty.call(outputs, portId)) {
      result[param] = outputs[portId];
    }
  }
  return result;
}
